<!-- 通用计算器渲染外壳 -->
<template>
	<div>
		<!-- config 异步加载完成前不渲染 -->
		<template v-if="ready">
		<div class="calc_shell_box">
			<!-- 左侧输入区 -->
			<div class="calc_left">
				<h3 class="calc_title">{{ config.name }}</h3>

				<!-- 动态渲染输入项 -->
				<template v-for="field in config.schema.inputs">
					<!-- condition 控制：不满足条件时隐藏 -->
					<template v-if="!field.condition || formData[field.condition.key] === field.condition.value">

						<!-- number 类型 -->
						<div v-if="field.type === 'number'" :key="field.key" class="form_line">
							<div class="form_label">{{ field.label }}</div>
							<div class="form_input">
								<el-input
									v-model="formData[field.key]"
									:placeholder="field.placeholder || ('请输入' + field.label)"
									@input="onInputChange()">
								</el-input>
							</div>
							<div class="form_unit">{{ field.unit || '' }}</div>
						</div>

						<!-- select 类型 -->
						<div v-else-if="field.type === 'select'" :key="field.key" class="form_line">
							<div class="form_label">{{ field.label }}</div>
							<div class="form_input">
								<el-select style="width:100%;" v-model="formData[field.key]" :placeholder="'请选择' + field.label" @change="onFieldChange">
									<el-option
										v-for="opt in field.options"
										:key="opt.value"
										:label="opt.label"
										:value="opt.value">
									</el-option>
								</el-select>
							</div>
							<div class="form_unit">{{ field.unit || '' }}</div>
						</div>

						<!-- tab-switch 类型 -->
						<div v-else-if="field.type === 'tab-switch'" :key="field.key" class="tab_switch">
							<div
								class="tab_item"
								:class="{ active: formData[field.key] === opt.value }"
								v-for="opt in field.options"
								:key="opt.value"
								@click="onTabSwitch(field.key, opt.value)">
								{{ opt.label }}
							</div>
						</div>

						<!-- switch 类型 -->
						<div v-else-if="field.type === 'switch'" :key="field.key" class="form_line form_switch_line">
							<div class="form_label">{{ field.label }}</div>
							<el-switch v-model="formData[field.key]" @change="onFieldChange"></el-switch>
						</div>

					</template>
				</template>

				<!-- 计算按钮 -->
				<div class="calc_btn" @click="startCalc">立即计算</div>
			</div>

			<!-- 右侧结果区 -->
			<div class="calc_right">
				<h3 class="calc_title">计算结果</h3>
				<div class="result_box">
					<!-- 结果项：用 template 包裹，避免 v-for + v-if 同元素 -->
					<template v-if="result_show">
						<template v-for="r in config.schema.results">
							<div
								v-if="results[r.key] !== null && results[r.key] !== undefined"
								:key="r.key"
								class="result_item"
								:class="{ result_highlight: r.highlight }">
								<div class="result_label">{{ r.label }}</div>
								<div class="result_value">{{ results[r.key] }}</div>
								<div class="result_unit">{{ r.unit || '' }}</div>
							</div>
						</template>
					</template>

					<!-- 公式说明 -->
					<div class="formula_box" v-if="result_show && (config.schema.formula || formulaText)">
						<div class="formula_title">计算公式</div>
						<div class="formula_text" v-for="(f, i) in (formulaText || config.schema.formula)" :key="i">{{ f }}</div>
					</div>

					<!-- 空状态 -->
					<div class="empty_tip" v-if="!result_show">
						请在左侧输入数据后点击计算
					</div>
				</div>
			</div>
		</div>

		<!-- SEO内容区 -->
		<div class="seo-content" v-if="config.schema.seo_sections">
			<div class="seo-section" v-for="(sec, i) in config.schema.seo_sections" :key="i">
				<h2>{{ sec.title }}</h2>
				<p v-html="sec.content"></p>
			</div>
		</div>
		</template>
	</div>
</template>

<script>
	import { CALC_REGISTRY } from './registry/calc_registry';
	import { execute_url } from '@/utils/execute_url';
	import { post } from '@/utils/request_two';

	export default {
		name: 'CalcShell',
		data() {
			return {
				ready: false,
				config: {},
				formData: {},
				results: {},
				engine: null,
				result_show: false,
				formulaText: null,
			};
		},
		async created() {
			// 从注册中心找到当前路由对应的配置
			this.config = CALC_REGISTRY.find(c => c.path === this.$route.path);
			if (!this.config || !this.config.schema) return;

			// 懒加载引擎
			if (this.config.calc_mode === 'local' && this.config.engine) {
				const mod = await this.config.engine();
				this.engine = mod.default;
			}

			// 初始化默认值
			this.config.schema.inputs.forEach(f => {
				this.$set(this.formData, f.key, f.default !== undefined ? f.default : '');
			});

			// 埋点统计
			if (this.config.seo && this.config.seo.title) {
				post(execute_url, { code: '600' });
			}

			// 标记配置加载完成，触发模板渲染
			this.ready = true;
		},
		methods: {
			/**
			 * 输入框内容变化
			 */
			onInputChange() {
				// 实时计算（如果引擎支持）
				if (this.engine && this.engine.autoCalc !== false) {
					this.doCalc();
				}
			},
			/**
			 * 字段变化（select等）
			 */
			onFieldChange() {
				if (this.engine && this.engine.autoCalc !== false && this.result_show) {
					this.doCalc();
				}
			},
			/**
			 * tab-switch 切换
			 */
			onTabSwitch(key, value) {
				this.formData[key] = value;
				if (this.result_show) {
					this.doCalc();
				}
			},
			/**
			 * 开始计算（按钮点击）
			 */
			startCalc() {
				// 验证必填项（跳过有condition且条件不满足的字段）
				const hasEmpty = this.config.schema.inputs.some(f => {
					// 如果字段有条件控制且条件不满足，跳过验证
					if (f.condition && this.formData[f.condition.key] !== f.condition.value) return false;
					// 只验证number类型且标记为必填（无placeholder提示可选的字段）
					if (f.type === 'number' && f.required !== false && !f.optional &&
						!f.placeholder?.includes('可选') && (!this.formData[f.key] || isNaN(Number(this.formData[f.key])))) {
						this.$message({ message: '请输入' + f.label, type: 'warning' });
						return true;
					}
					return false;
				});
				if (hasEmpty) return;

				this.doCalc();
			},
			/**
			 * 执行计算
			 */
			doCalc() {
				if (!this.engine) return;

				const result = this.engine.calc({ ...this.formData });
				if (result) {
					this.results = result;
					this.result_show = true;

					// 公式说明
					if (this.engine.getFormula) {
						this.formulaText = this.engine.getFormula({ ...this.formData });
					}
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	@import './calc-common.scss';
</style>
